import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { appointmentRepo, productRepo, resourceRepo, ORDER_TYPE } from "../../services";
import { nextRowCode, resolveBranchId } from "../../services/commerce";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { useMockCustomer } from "./useMockCustomer";
import PaymentQrModal from "../../components/PaymentQrModal";
import { Button, Card, Input, Select, baht, useAsync } from "../../components/ui";

const SLOTS = ["09:00", "10:00", "11:00", "13:00", "14:00", "15:00", "16:00", "17:00"];
const DEPOSIT_RATE = 0.2;

export default function Booking() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { orgId, branchId: activeBranchId, branches, toast } = useApp();
  const { tx } = useI18n();
  const { customer } = useMockCustomer(orgId);

  const { data: products } = useAsync(() => productRepo.all(orgId, (row) => row.type === "service" || row.type === "package"), [orgId], []);
  const { data: resources } = useAsync(() => resourceRepo.all(orgId), [orgId], []);

  const [productId, setProductId] = useState(state?.productId || "");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState(SLOTS[0]);
  const [resourceId, setResourceId] = useState("");
  const [pickedBranch, setPickedBranch] = useState("");
  const [showQr, setShowQr] = useState(false);
  const [createdAppointment, setCreatedAppointment] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // หลายสาขา: ให้ลูกค้าเลือก (ค่าเริ่มต้น = สาขาที่เลือกอยู่/สาขาหลัก) · ไม่ใช้ branches[0]
  const branchId = pickedBranch || resolveBranchId(branches, activeBranchId);
  const product = products?.find((row) => row.id === productId);
  const deposit = product ? Math.round(product.price * DEPOSIT_RATE) : 0;

  const submit = async () => {
    if (submitting) return; // กันดับเบิลคลิก/แตะรัว ๆ สร้างนัดหมายซ้ำ
    if (!product || !customer) return toast(tx("กรุณาเลือกบริการ", "Please select a service"), "error");
    if (branches.length > 0 && !branchId) return toast(tx("กรุณาเลือกสาขา", "Please select a branch"), "error");
    setSubmitting(true);
    try {
      const appointment = await appointmentRepo.create(orgId, {
        code: await nextRowCode(appointmentRepo, orgId, { prefix: "AP", docType: "appointment" }),
        customerId: customer.id,
        customerName: customer.name,
        customerPhone: customer.phone,
        productId: product.id,
        serviceName: product.name,
        resourceId,
        branchId,
        startAt: new Date(`${date}T${time}`).toISOString(),
        durationMin: product.duration || 60,
        status: "pending",
        price: product.price,
        depositAmount: deposit,
        depositPaid: false,
      });
      setCreatedAppointment(appointment);
      if (deposit > 0) setShowQr(true);
      else {
        toast(tx("จองคิวสำเร็จ", "Booking confirmed"), "success");
        navigate("/app/bookings");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onDepositPaid = async () => {
    await appointmentRepo.update(orgId, createdAppointment.id, { depositPaid: true, status: "confirmed" });
    toast(tx("จองคิวและชำระมัดจำสำเร็จ", "Booking confirmed and deposit paid"), "success");
    navigate("/app/bookings");
  };

  return (
    <>
      <Card title={tx("จองคิว", "Book an appointment")}>
        <Select label={tx("เลือกบริการ", "Service")} placeholder={tx("เลือกบริการ", "Select service")} options={(products || []).map((p) => ({ value: p.id, label: `${p.name} (฿${p.price.toLocaleString()})` }))} value={productId} onChange={(e) => setProductId(e.target.value)} />
        {branches.length > 1 ? (
          <Select label={tx("สาขา", "Branch")} placeholder={tx("เลือกสาขา", "Select branch")} options={branches.map((b) => ({ value: b.id, label: b.name }))} value={branchId} onChange={(e) => setPickedBranch(e.target.value)} />
        ) : null}
        <Input label={tx("วันที่", "Date")} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <Select label={tx("เวลา", "Time")} options={SLOTS} value={time} onChange={(e) => setTime(e.target.value)} />
        <Select label={tx("สถานที่/ทรัพยากร", "Resource")} placeholder={tx("เลือกทรัพยากร (ถ้ามี)", "Select resource (optional)")} options={(resources || []).map((r) => ({ value: r.id, label: r.name }))} value={resourceId} onChange={(e) => setResourceId(e.target.value)} />
      </Card>

      {product ? (
        <Card>
          <div className="u-between"><span className="u-fs-13 u-muted">{tx("ราคาบริการ", "Service price")}</span><span>{baht(product.price)}</span></div>
          {deposit > 0 ? <div className="u-between u-mt-8"><span className="u-fs-13 u-muted">{tx("มัดจำที่ต้องชำระ", "Deposit required")}</span><span className="u-strong u-indigo">{baht(deposit)}</span></div> : null}
        </Card>
      ) : null}

      <Button variant="primary" block onClick={submit} disabled={submitting}>
        {submitting ? tx("กำลังบันทึก...", "Saving...") : deposit > 0 ? tx("จองและชำระมัดจำ", "Book & pay deposit") : tx("ยืนยันการจอง", "Confirm booking")}
      </Button>

      <PaymentQrModal
        open={showQr}
        title={tx("ชำระมัดจำ", "Deposit payment")}
        subtitle={product?.name}
        amount={deposit}
        orderType={ORDER_TYPE.bookingDeposit}
        reference={createdAppointment?.code}
        items={[{ productName: `${tx("มัดจำ", "Deposit")} - ${product?.name}`, quantity: 1, price: deposit }]}
        onClose={() => setShowQr(false)}
        onPaid={onDepositPaid}
      />
    </>
  );
}
